import {
  EConditionKind,
  EConditionOp,
} from '@hookpipe/contracts/flows/schemas/flow-condition.schema';

export type TScalar = string | number | boolean | null;
export type TValue = TScalar | TScalar[];

export interface IConditionRow {
  path: string;
  kind: EConditionKind;
  op: EConditionOp;
  value?: TValue;
}

export interface IConditionGroup {
  conditions: IConditionRow[];
}

export interface IConditionForm {
  groups: IConditionGroup[];
}

export const OP_LABEL: Record<EConditionOp, string> = {
  [EConditionOp.IS]: 'is',
  [EConditionOp.IS_NOT]: 'is not',
  [EConditionOp.CONTAINS]: 'contains',
  [EConditionOp.NOT_CONTAINS]: 'does not contain',
  [EConditionOp.CONTAINS_ANY]: 'contains any of',
  [EConditionOp.STARTS_WITH]: 'starts with',
  [EConditionOp.ENDS_WITH]: 'ends with',
  [EConditionOp.MATCHES]: 'matches pattern',
  [EConditionOp.IS_ONE_OF]: 'is one of',
  [EConditionOp.IS_NOT_ONE_OF]: 'is not one of',
  [EConditionOp.GT]: 'is more than',
  [EConditionOp.GTE]: 'is at least',
  [EConditionOp.LT]: 'is less than',
  [EConditionOp.LTE]: 'is at most',
  [EConditionOp.BETWEEN]: 'is between',
  [EConditionOp.IS_TRUE]: 'is true',
  [EConditionOp.IS_FALSE]: 'is false',
  [EConditionOp.HAS_MORE_THAN]: 'has more than',
  [EConditionOp.IS_EMPTY]: 'is empty',
  [EConditionOp.IS_NOT_EMPTY]: 'is not empty',
  [EConditionOp.EXISTS]: 'exists',
  [EConditionOp.NOT_EXISTS]: 'does not exist',
};

export const emptyRow = (): IConditionRow => ({
  path: '',
  kind: EConditionKind.TEXT,
  op: EConditionOp.IS,
  value: '',
});

const KINDS = Object.values(EConditionKind) as string[];
const OPS = Object.values(EConditionOp) as string[];

const isScalar = (value: unknown): value is TScalar =>
  value === null || ['string', 'number', 'boolean'].includes(typeof value);

const coerceRow = (raw: unknown): IConditionRow => {
  const row = (raw ?? {}) as Record<string, unknown>;
  const kind = KINDS.includes(row.kind as string)
    ? (row.kind as EConditionKind)
    : EConditionKind.TEXT;
  const op = OPS.includes(row.op as string) ? (row.op as EConditionOp) : EConditionOp.IS;
  const value = Array.isArray(row.value)
    ? row.value.filter(isScalar)
    : isScalar(row.value)
      ? row.value
      : undefined;
  return { path: typeof row.path === 'string' ? row.path : '', kind, op, value };
};

// Whatever the field held — nothing, half a form, JSON typed by hand — becomes something the
// builder can draw; what it cannot read it drops rather than refuses to open.
export const coerceForm = (raw: unknown): IConditionForm => {
  const groups = (raw as { groups?: unknown } | null)?.groups;
  const drawn = Array.isArray(groups)
    ? groups
        .map((group) => (group as { conditions?: unknown } | null)?.conditions)
        .filter((conditions): conditions is unknown[] => Array.isArray(conditions))
        .map((conditions) => ({ conditions: conditions.map(coerceRow) }))
        .filter((group) => group.conditions.length > 0)
    : [];
  return { groups: drawn.length > 0 ? drawn : [{ conditions: [emptyRow()] }] };
};

// A short read of the condition, not a full render of it: one row says itself, more say the count.
export const conditionSummary = (raw: unknown): string => {
  const rows = coerceForm(raw).groups.flatMap((group) => group.conditions);
  if (rows.length === 0) return 'a condition';
  if (rows.length > 1) return `${rows.length} conditions`;
  const [row] = rows;
  const value = Array.isArray(row.value) ? row.value.join(', ') : (row.value ?? '');
  return `${row.path} ${OP_LABEL[row.op]}${value === '' ? '' : ` ${value}`}`;
};

export const loopHeader = (config: Record<string, unknown>): string => {
  const max = typeof config.maxIterations === 'number' ? config.maxIterations : undefined;
  return `while ${conditionSummary(config.while)}${max ? ` · at most ${max}` : ''}`;
};
